import React, { useState, useEffect, useMemo } from 'react';
import Header from '../../components/Header';
import { styles, colors, shadows, radius } from '../../styles/theme';
import useUrlState from '../../hooks/useUrlState';
import SearchableSelect from '../../components/SearchableSelect';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

export default function PersonelYemegiPage() {
  // Aktif görünüm (form/list) URL'e bağlı: Kayıtlar sekmesindeyken başka sayfaya gidip
  // geri dönünce / yenileyince Kayıtlar açık kalır, menüden açınca form.
  const [view, setView] = useUrlState('view', 'form');
  const [recipes, setRecipes] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [gecmis, setGecmis] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  // Form state
  const [lokasyonId, setLokasyonId] = useState('');
  const [tarih, setTarih] = useState(new Date().toISOString().split('T')[0]);
  const [selectedRecipeId, setSelectedRecipeId] = useState('');
  const [adet, setAdet] = useState(1);
  const [kalemler, setKalemler] = useState([]);
  const [notlar, setNotlar] = useState('');

  // Filtre state (URL'e bağlı — Kayıtlar listesine geri dönünce korunur)
  const [filtreLokasyon, setFiltreLokasyon] = useUrlState('flokasyon', '');
  const [filtreBaslangic, setFiltreBaslangic] = useUrlState('fbas', '');
  const [filtreBitis, setFiltreBitis] = useUrlState('fbit', '');

  useEffect(() => {
    fetch(`${API_URL}/api/recipes?durum=AKTIF`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(data => {
        if (Array.isArray(data)) setRecipes(data);
      }).catch(() => {});
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(data => {
        if (Array.isArray(data)) setLokasyonlar(data);
      }).catch(() => {});
    // Mount'ta URL'deki filtrelerle yükle (deep-link / geri dönüş filtreleri uygulanır)
    loadGecmis(filtreLokasyon, filtreBaslangic, filtreBitis);
    // eslint-disable-next-line
  }, []);

  const loadGecmis = (lokId, bas, bit) => {
    const params = new URLSearchParams();
    if (lokId) params.append('lokasyon_id', lokId);
    if (bas) params.append('baslangic', bas);
    if (bit) params.append('bitis', bit);
    const qs = params.toString();
    fetch(`${API_URL}/api/personel-yemegi${qs ? '?' + qs : ''}`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(data => {
        if (Array.isArray(data)) setGecmis(data);
      }).catch(() => {});
  };

  // Only show TABAK type recipes (menu items)
  const tabakRecipes = useMemo(() =>
    recipes.filter(r => r.tip === 'TABAK'),
    [recipes]
  );

  const recipeOptions = useMemo(() =>
    tabakRecipes.map(r => ({ value: r.id, label: r.ad })),
    [tabakRecipes]
  );

  const lokasyonOptions = useMemo(() =>
    (lokasyonlar || []).filter(l => l.aktif !== false).map(l => ({ value: l.id, label: l.ad })),
    [lokasyonlar]
  );

  const handleAddKalem = () => {
    if (!selectedRecipeId || adet <= 0) return;
    const recipe = tabakRecipes.find(r => r.id === selectedRecipeId);
    if (!recipe) return;
    // Check if already added
    const existing = kalemler.find(k => k.recipe_id === selectedRecipeId);
    if (existing) {
      setKalemler(kalemler.map(k =>
        k.recipe_id === selectedRecipeId ? { ...k, adet: k.adet + adet } : k
      ));
    } else {
      setKalemler([...kalemler, { recipe_id: recipe.id, recipe_ad: recipe.ad, adet }]);
    }
    setSelectedRecipeId('');
    setAdet(1);
  };

  const handleRemoveKalem = (recipeId) => {
    setKalemler(kalemler.filter(k => k.recipe_id !== recipeId));
  };

  const handleSubmit = async () => {
    if (!lokasyonId) { setMsg({ type: 'error', text: 'Lokasyon secin' }); return; }
    if (kalemler.length === 0) { setMsg({ type: 'error', text: 'En az bir urun ekleyin' }); return; }
    setLoading(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/personel-yemegi`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          lokasyon_id: lokasyonId,
          tarih,
          kalemler: kalemler.map(k => ({ recipe_id: k.recipe_id, adet: k.adet })),
          notlar: notlar || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Hata olustu');
      setMsg({
        type: 'success',
        text: `Kaydedildi: ${data.toplam_porsiyon} porsiyon, ${data.hammadde_sayisi} hammadde stoktan dusuldu. Maliyet: ${(data.toplam_maliyet || 0).toFixed(2)} TL`
      });
      setKalemler([]);
      setNotlar('');
      loadGecmis();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydi silmek istediginize emin misiniz? Stok hareketleri geri alinacak.')) return;
    try {
      const res = await fetch(`${API_URL}/api/personel-yemegi/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Silinemedi');
      }
      setMsg({ type: 'success', text: 'Kayit silindi, stok geri yuklendi' });
      loadGecmis(filtreLokasyon, filtreBaslangic, filtreBitis);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const handleFiltreUygula = () => {
    loadGecmis(filtreLokasyon, filtreBaslangic, filtreBitis);
  };

  // KPI: Aylik toplam
  const thisMonth = new Date().toISOString().slice(0, 7);
  const aylikKayitlar = gecmis.filter(g => g.tarih && g.tarih.startsWith(thisMonth));
  const aylikMaliyet = aylikKayitlar.reduce((s, g) => s + (g.toplam_maliyet || 0), 0);
  const aylikPorsiyon = aylikKayitlar.reduce((s, g) => s + (g.toplam_porsiyon || 0), 0);

  const toplamPorsiyon = kalemler.reduce((s, k) => s + k.adet, 0);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <Header />
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
        {/* Title + View Toggle */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Personel Yemegi</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setView('form')}
              style={{
                padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: view === 'form' ? colors.primary : '#e0e0e0',
                color: view === 'form' ? '#fff' : '#333', fontWeight: 600, fontSize: 13
              }}
            >Yeni Kayit</button>
            <button
              onClick={() => { setView('list'); loadGecmis(filtreLokasyon, filtreBaslangic, filtreBitis); }}
              style={{
                padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: view === 'list' ? colors.primary : '#e0e0e0',
                color: view === 'list' ? '#fff' : '#333', fontWeight: 600, fontSize: 13
              }}
            >Gecmis ({gecmis.length})</button>
          </div>
        </div>

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div style={{ ...styles.card, padding: 16, textAlign: 'center', background: '#fff', borderRadius: radius.lg, boxShadow: shadows.sm }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: colors.primary }}>{aylikPorsiyon}</div>
            <div style={{ fontSize: 12, color: colors.textSecondary }}>Bu Ay Porsiyon</div>
          </div>
          <div style={{ ...styles.card, padding: 16, textAlign: 'center', background: '#fff', borderRadius: radius.lg, boxShadow: shadows.sm }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: colors.warning }}>{aylikMaliyet.toFixed(2)} TL</div>
            <div style={{ fontSize: 12, color: colors.textSecondary }}>Bu Ay Maliyet</div>
          </div>
          <div style={{ ...styles.card, padding: 16, textAlign: 'center', background: '#fff', borderRadius: radius.lg, boxShadow: shadows.sm }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: colors.info }}>{gecmis.length}</div>
            <div style={{ fontSize: 12, color: colors.textSecondary }}>Toplam Kayit</div>
          </div>
        </div>

        {/* Message */}
        {msg && (
          <div style={{
            padding: '12px 16px', borderRadius: 8, marginBottom: 16,
            background: msg.type === 'success' ? colors.successLight : colors.dangerLight,
            color: msg.type === 'success' ? '#2e7d32' : '#c62828',
            border: `1px solid ${msg.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`
          }}>
            {msg.text}
          </div>
        )}

        {/* FORM VIEW */}
        {view === 'form' && (
          <div style={{ background: '#fff', borderRadius: radius.lg, boxShadow: shadows.sm, padding: 24 }}>
            <h3 style={{ marginTop: 0, marginBottom: 20, fontSize: 16, fontWeight: 600 }}>Yeni Personel Yemegi Kaydi</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>Lokasyon *</label>
                <SearchableSelect
                  options={lokasyonOptions}
                  value={lokasyonId}
                  onChange={setLokasyonId}
                  placeholder="Lokasyon secin..."
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>Tarih</label>
                <input
                  type="date"
                  value={tarih}
                  onChange={e => setTarih(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
                    borderRadius: 6, fontSize: 14, boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Recipe selector + adet + add */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>Urun Ekle (Tabak Receteleri)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <SearchableSelect
                    options={recipeOptions}
                    value={selectedRecipeId}
                    onChange={setSelectedRecipeId}
                    placeholder="Recete arayin..."
                  />
                </div>
                <input
                  type="number"
                  min="1"
                  value={adet}
                  onChange={e => setAdet(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{
                    width: 70, padding: '8px 12px', border: `1px solid ${colors.border}`,
                    borderRadius: 6, fontSize: 14, textAlign: 'center', boxSizing: 'border-box'
                  }}
                />
                <button
                  onClick={handleAddKalem}
                  disabled={!selectedRecipeId}
                  style={{
                    padding: '8px 20px', background: selectedRecipeId ? colors.success : '#ccc',
                    color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600,
                    cursor: selectedRecipeId ? 'pointer' : 'default', fontSize: 14, whiteSpace: 'nowrap'
                  }}
                >Ekle</button>
              </div>
            </div>

            {/* Kalemler tablosu */}
            {kalemler.length > 0 && (
              <div style={{ marginBottom: 16, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #e0e0e0' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Urun</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, width: 80 }}>Adet</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {kalemler.map(k => (
                      <tr key={k.recipe_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 500 }}>{k.recipe_ad}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700 }}>{k.adet}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleRemoveKalem(k.recipe_id)}
                            style={{
                              background: 'none', border: 'none', color: colors.danger,
                              cursor: 'pointer', fontSize: 16, fontWeight: 700
                            }}
                          >x</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: colors.backgroundAlt }}>
                      <td style={{ padding: '10px 12px', fontWeight: 700 }}>Toplam</td>
                      <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700 }}>{toplamPorsiyon}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Notlar */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>Notlar</label>
              <textarea
                value={notlar}
                onChange={e => setNotlar(e.target.value)}
                placeholder="Opsiyonel not..."
                rows={2}
                style={{
                  width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
                  borderRadius: 6, fontSize: 14, boxSizing: 'border-box', resize: 'vertical'
                }}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading || kalemler.length === 0}
              style={{
                padding: '12px 32px',
                background: loading || kalemler.length === 0 ? '#999' : colors.primary,
                color: '#fff', border: 'none', borderRadius: 8, fontSize: 15,
                fontWeight: 700, cursor: loading || kalemler.length === 0 ? 'default' : 'pointer',
                width: '100%'
              }}
            >
              {loading ? 'Kaydediliyor...' : 'Kaydet ve Stoktan Dus'}
            </button>
          </div>
        )}

        {/* LIST VIEW */}
        {view === 'list' && (
          <div>
            {/* Filtreler */}
            <div style={{ background: '#fff', borderRadius: radius.lg, boxShadow: shadows.sm, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: colors.textSecondary }}>Lokasyon</label>
                  <select
                    value={filtreLokasyon}
                    onChange={e => setFiltreLokasyon(e.target.value)}
                    style={{
                      width: '100%', padding: '6px 8px', border: `1px solid ${colors.border}`,
                      borderRadius: 6, fontSize: 13, boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Tumunu Goster</option>
                    {lokasyonlar.filter(l => l.aktif !== false).map(l => (
                      <option key={l.id} value={l.id}>{l.ad}</option>
                    ))}
                  </select>
                </div>
                <div style={{ minWidth: 130 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: colors.textSecondary }}>Baslangic</label>
                  <input type="date" value={filtreBaslangic} onChange={e => setFiltreBaslangic(e.target.value)}
                    style={{ padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ minWidth: 130 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: colors.textSecondary }}>Bitis</label>
                  <input type="date" value={filtreBitis} onChange={e => setFiltreBitis(e.target.value)}
                    style={{ padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
                <button
                  onClick={handleFiltreUygula}
                  style={{
                    padding: '7px 16px', background: colors.primary, color: '#fff',
                    border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer'
                  }}
                >Filtrele</button>
              </div>
            </div>

            {/* Tablo */}
            <div style={{ background: '#fff', borderRadius: radius.lg, boxShadow: shadows.sm, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Tarih</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Lokasyon</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600 }}>Porsiyon</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>Maliyet</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600 }}>Detay</th>
                  </tr>
                </thead>
                <tbody>
                  {gecmis.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: colors.textTertiary }}>Henuz kayit yok</td></tr>
                  ) : gecmis.map(g => (
                    <React.Fragment key={g.id}>
                      <tr style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                          onClick={() => setExpandedId(expandedId === g.id ? null : g.id)}>
                        <td style={{ padding: '10px 12px' }}>{g.tarih}</td>
                        <td style={{ padding: '10px 12px' }}>{g.lokasyon_ad}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700 }}>{g.toplam_porsiyon}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: colors.warning }}>
                          {(g.toplam_maliyet || 0).toFixed(2)} TL
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <span style={{ fontSize: 16 }}>{expandedId === g.id ? '\u25B2' : '\u25BC'}</span>
                        </td>
                      </tr>
                      {expandedId === g.id && (
                        <tr>
                          <td colSpan={5} style={{ padding: 0, background: '#fafbfc' }}>
                            <div style={{ padding: '12px 16px' }}>
                              {g.notlar && <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 8 }}>Not: {g.notlar}</div>}
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Urun</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>Adet</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Birim Maliyet</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Toplam</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(g.kalemler || []).map((kl, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                      <td style={{ padding: '6px 8px' }}>{kl.recipe_ad}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>{kl.adet}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(kl.birim_maliyet || 0).toFixed(2)} TL</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{(kl.toplam_maliyet || 0).toFixed(2)} TL</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <div style={{ marginTop: 8, textAlign: 'right' }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDelete(g.id); }}
                                  style={{
                                    padding: '4px 12px', background: colors.danger, color: '#fff',
                                    border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600,
                                    cursor: 'pointer'
                                  }}
                                >Sil</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
